import { get_token } from "@/router";
import { normalize_properties_response } from "@/components/Settings/settings_utils";
import { filter_live_active_properties } from "@/components/Settings/property_access_utils";
import {
    get_user_activity_fields,
    get_user_names,
    get_user_phone_number,
    get_user_role_fields
} from "@/components/SettingsUserManagementTab/settings_user_management_utils";
import {
    fetch_user_property_assignments,
    get_assignment_properties,
    get_assignment_property_ids
} from "@/utils/user_property_assignments";
import {
    extract_collection,
    get_auth_headers,
    get_fetch_error_message,
    get_response_data
} from "./settings_user_management_api";

function normalize_property_record(property) {
    const property_names = [
        property?.name,
        property?.property_name,
        property?.title,
        "Unnamed Property"
    ];

    return ({
        id: property?.property_id ?? property?.id ?? "",
        name: property_names.find(Boolean)
    });
}

function build_property_from_value(property, property_options) {
    const is_scalar_property = typeof property === "number" || typeof property === "string";
    return is_scalar_property
        ? find_property_option(property_options, property) || { id: property, name: String(property) }
        : normalize_property_record(property);
}

function find_property_option(property_options, property_id) {
    return property_options.find((property) => String(property.id) === String(property_id)) || null;
}

function normalize_property_values(values, property_options) {
    const properties = Array.isArray(values) ? values : [];

    return properties
        .map((property) => build_property_from_value(property, property_options))
        .filter((property) => property.id !== "" && property.name);
}

function unique_properties(properties) {
    return properties.filter((property, index, all_properties) => {
        return all_properties.findIndex((existing_property) => String(existing_property.id) === String(property.id)) === index;
    });
}

function filter_available_properties(properties, property_options) {
    return properties.filter((property) => {
        return property_options.some((property_option) => String(property_option.id) === String(property.id));
    });
}

function is_live_active_assignment(assignment, live_property_ids) {
    return assignment.is_active && live_property_ids.includes(String(assignment.property_id));
}

function get_user_property_values(user) {
    return [
        user?.assigned_properties,
        user?.properties,
        user?.property_assignments,
        user?.profile?.properties,
        []
    ].find((value) => Array.isArray(value));
}

function get_user_property_ids(user) {
    return [
        user?.property_ids,
        user?.assigned_property_ids,
        user?.profile?.property_ids,
        []
    ].find((value) => Array.isArray(value));
}

function get_missing_roles_message(roles_url, auth_token) {
    return !roles_url || !auth_token
        ? "Roles endpoint or auth token missing."
        : "";
}

function get_missing_users_message(users_list_url, auth_token) {
    if (!users_list_url) return "VUE_APP_USER_LIST_URL is not configured. Showing available local profile data.";
    if (!auth_token) return "No auth token found. Showing available local profile data.";
    return "";
}

function get_users_from_response(users_data) {
    const users = extract_collection(users_data, ["data", "results", "users"]);
    return Array.isArray(users) ? users : [];
}

function get_next_role_options(roles, fallback_roles) {
    return Array.isArray(roles) && roles.length ? roles : fallback_roles;
}

function warn_missing_roles_url(roles_url) {
    if (!roles_url) console.warn("VUE_APP_ROLES_URL is not configured.");
}

function apply_fallback_roles(view_model, missing_message) {
    if (!missing_message) {
        return false;
    }

    warn_missing_roles_url(view_model.roles_url);
    view_model.role_options = view_model.get_fallback_role_options();
    return true;
}

async function fetch_roles(roles_url) {
    const response = await fetch(roles_url, {
        method: "GET",
        headers: get_auth_headers(false)
    });
    const roles_data = await get_response_data(response, "Unable to load roles.");

    return extract_collection(roles_data, ["data", "results", "roles"]);
}

export const settingsUserManagementDataMethods = {
    get_role_name(user) {
        return user?.user_role?.role?.name || "";
    },

    get_user_role_id(user) {
        return user?.user_role?.role?.id ?? null;
    },

    get_user_row(user) {
        const assigned_properties = this.get_assigned_properties(user);

        return ({
            id: user?.id ?? "",
            ...get_user_names(user),
            phone_number: get_user_phone_number(user),
            ...get_user_role_fields(user),
            property_ids: assigned_properties.map((property) => property.id).filter(Boolean),
            assigned_properties,
            ...get_user_activity_fields(user),
            raw: user
        });
    },

    async enrich_user_with_property_assignments(user) {
        if (!user?.id) {
            return user;
        }

        const assignments = await fetch_user_property_assignments(user.id, get_auth_headers(false), {
            include_inactive: true
        });
        const live_property_ids = this.property_options.map((property) => String(property.id));
        const active_assignments = assignments.filter((assignment) => is_live_active_assignment(assignment, live_property_ids));

        return ({
            ...user,
            property_assignments: assignments,
            property_ids: get_assignment_property_ids(active_assignments),
            assigned_properties: get_assignment_properties(active_assignments, this.property_options)
        });
    },

    get_assigned_properties(user) {
        const normalized_properties = [
            ...normalize_property_values(get_user_property_values(user), this.property_options),
            ...normalize_property_values(get_user_property_ids(user), this.property_options)
        ];

        return filter_available_properties(unique_properties(normalized_properties), this.property_options);
    },

    find_property_option(property_id) {
        return this.property_options.find((property) => String(property.id) === String(property_id)) || null;
    },

    get_fallback_users() {
        return this.user_profile ? [this.get_user_row(this.user_profile)] : [];
    },

    get_fallback_role_options() {
        const current_user_role = this.user_profile?.user_role?.role || null;
        return current_user_role ? [current_user_role] : [];
    },

    async load_initial_data() {
        await this.load_roles();
        await this.load_properties();
        await this.load_users();
    },

    async load_roles() {
        this.is_loading_roles = true;

        try {
            const auth_token = get_token();
            const missing_message = get_missing_roles_message(this.roles_url, auth_token);

            if (apply_fallback_roles(this, missing_message)) {
                return;
            }

            const roles = await fetch_roles(this.roles_url);
            this.role_options = get_next_role_options(roles, this.get_fallback_role_options());
        } catch {
            this.role_options = this.get_fallback_role_options();
        } finally {
            this.is_loading_roles = false;
        }
    },

    async load_users() {
        this.is_loading = true;
        this.load_error = "";

        try {
            const auth_token = get_token();
            const missing_message = get_missing_users_message(this.users_list_url, auth_token);

            if (missing_message) {
                this.load_error = missing_message;
                this.set_users(this.get_fallback_users(), false);
                return;
            }

            const response = await fetch(this.users_list_url, {
                method: "GET",
                headers: get_auth_headers(false)
            });
            const users_data = await get_response_data(response, "Unable to load users.");
            const users_with_assignments = await Promise.all(
                get_users_from_response(users_data).map((user) => this.enrich_user_with_property_assignments(user))
            );
            const next_users = users_with_assignments.map((user) => this.get_user_row(user));

            this.set_users(next_users.length ? next_users : this.get_fallback_users(), false);
        } catch (error) {
            this.set_users(this.get_fallback_users(), false);
            this.load_error = `${get_fetch_error_message(error, "Unable to load users from the server.")} Showing available local profile data.`;
        } finally {
            this.is_loading = false;
            this.$nextTick(() => {
                this.rebuild_tables();
            });
        }
    },

    async load_properties() {
        this.is_loading_properties = true;

        try {
            const auth_token = get_token();

            if (!this.properties_url || !auth_token) {
                this.property_options = [];
                return;
            }

            const response = await fetch(this.properties_url, {
                method: "GET",
                headers: get_auth_headers(false)
            });
            const properties_data = await get_response_data(response, "Unable to load properties.");
            const properties = extract_collection(properties_data, ["data", "results", "properties"]);

            this.property_options = filter_live_active_properties(normalize_properties_response(properties));
        } catch {
            this.property_options = [];
        } finally {
            this.is_loading_properties = false;
        }
    }
};
